/**
 * Configurations for getDynamicTokens, setDynamicTokens, and useDynamicTokens.
 * Collated in this file for legibility.
 */

import type { RefObject } from 'react';

import type { ColorMode } from '@trello/theme';

export const DYNAMIC_TOKEN_ENTRY_POINT_MAP = {
  'dynamic.background': [
    'dynamic.background',
    'dynamic.background.transparent',
  ],
  'dynamic.button': [
    'dynamic.button',
    'dynamic.button.hovered',
    'dynamic.button.pressed',
    'dynamic.button.highlighted',
    'dynamic.button.highlighted.text',
    'dynamic.button.highlighted.hovered',
  ],
  'dynamic.navitem': [
    'dynamic.navitem',
    'dynamic.navitem.hovered',
    'dynamic.navitem.pressed',
  ],
  'dynamic.star': ['dynamic.star'],
  'dynamic.text': ['dynamic.icon', 'dynamic.text', 'dynamic.text.transparent'],
} as const;

export type DynamicTokenEntryPointMap = typeof DYNAMIC_TOKEN_ENTRY_POINT_MAP;
export type DynamicTokenEntryPoint = keyof DynamicTokenEntryPointMap;

/**
 * A unified interface for all types of background inputs (e.g. board, card).
 */
export interface Background {
  backgroundBrightness: 'light' | 'dark' | 'unknown';
  backgroundColor?: string;
  /**
   * If the background color was derived from an image, we want to generate
   * background colors with valid contrast; otherwise, we can just use opacity.
   */
  isBackgroundImage?: boolean;
}

export interface GetDynamicTokensConfig {
  /**
   * The background color or brightness to calculate our dynamic colors against.
   * A null value indicates that there is no background, and the getters will
   * instead set the tokens to falsy values.
   */
  background: Background | null;
  /**
   * The global theme. When calling the vanilla `setDynamicTokens` manually,
   * it must be wrapped with a `GlobalThemeObserver` to be made reactive!
   */
  colorMode: ColorMode;
  /**
   * If the user is in global dark theme, we can optionally fall back to
   * preset values regardless of the given background brightness.
   * @default false
   */
  shouldUseDarkThemeOverride?: boolean;
}

export interface SetDynamicTokensConfig extends GetDynamicTokensConfig {
  /**
   * The HTML element on which to assign tokens.
   */
  element: HTMLElement;
  /**
   * An array of all of the dynamic tokens to be assigned.
   * For example, if we want the dynamic button and text colors:
   * dynamicTokens: ['dynamic.button', 'dynamic.text']
   *
   * The list of valid values can be found in `getDynamicTokens.ts`.
   */
  dynamicTokens: Array<DynamicTokenEntryPoint>;
}

export interface UseDynamicTokensConfig
  extends Omit<SetDynamicTokensConfig, 'element' | 'colorMode'> {
  /**
   * A React Ref wrapper around the element.
   */
  ref: RefObject<HTMLElement>;
}
